import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardWrapper from '../../components/DashboardWrapper';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import PasswordInput from '../../components/PasswordInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, AlertCircle, Building2, User, Save, Loader2, Store, Phone, Calendar, MapPin, Users, Euro, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import api from '../../lib/api';
import { toast } from 'sonner';

const AdminEditUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [partners, setPartners] = useState([]);
  const [formData, setFormData] = useState({
    // Identité
    pseudo: '',
    prenom_utilisateur: '',
    nom_utilisateur: '',
    email: '',
    telephone: '',
    date_naissance: '',
    // Système
    role: 'retailer',
    partner_id: '',
    is_active: true,
    is_validated: false,
    password: '',
    // Magasin
    nom_magasin: '',
    raison_sociale: '',
    siret: '',
    adresse_magasin: '',
    code_postal: '',
    ville: '',
    date_creation_magasin: '',
    zone_implantation: '',
    surface_magasin: '',
    nb_salaries: '',
    ca_magasin: '',
    jeu_libre: null,
    animations_magasin: null,
    capacite_accueil: '',
    site_ecommerce: null,
    // Préférences
    newsletter: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    loadUser();
    loadPartners();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPartners = async () => {
    try {
      const response = await api.get('/partners');
      const sortedPartners = (response.data || [])
        .slice()
        .sort((a, b) => {
          const nameA = (a.nom || a.name || '').toLowerCase().trim();
          const nameB = (b.nom || b.name || '').toLowerCase().trim();
          return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });
      setPartners(sortedPartners);
    } catch (error) {
      console.error('Erreur chargement partenaires:', error);
    }
  };

  const loadUser = async () => {
    try {
      const response = await api.get(`/users/${userId}`);
      const u = response.data;
      setUser(u);
      setFormData({
        pseudo: u.pseudo || '',
        prenom_utilisateur: u.prenom_utilisateur || '',
        nom_utilisateur: u.nom_utilisateur || '',
        email: u.email || '',
        telephone: u.telephone || '',
        date_naissance: u.date_naissance || '',
        role: u.role || 'retailer',
        partner_id: u.partner_id || '',
        is_active: u.is_active !== undefined ? u.is_active : true,
        is_validated: u.is_validated !== undefined ? u.is_validated : false,
        password: '',
        nom_magasin: u.nom_magasin || '',
        raison_sociale: u.raison_sociale || '',
        siret: u.siret || '',
        adresse_magasin: u.adresse_magasin || '',
        code_postal: u.code_postal || '',
        ville: u.ville || '',
        date_creation_magasin: u.date_creation_magasin || '',
        zone_implantation: u.zone_implantation || '',
        surface_magasin: u.surface_magasin || '',
        nb_salaries: u.nb_salaries || '',
        ca_magasin: u.ca_magasin || '',
        jeu_libre: u.jeu_libre ?? null,
        animations_magasin: u.animations_magasin ?? null,
        capacite_accueil: u.capacite_accueil || '',
        site_ecommerce: u.site_ecommerce,
        newsletter: u.newsletter || false,
      });
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'utilisateur');
      navigate('/dashboard/admin/utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.pseudo?.trim()) errors.pseudo = 'Le pseudo est obligatoire';
    if (!formData.prenom_utilisateur?.trim()) errors.prenom_utilisateur = 'Le prénom est obligatoire';
    if (!formData.nom_utilisateur?.trim()) errors.nom_utilisateur = 'Le nom de famille est obligatoire';
    if (formData.role === 'partenaire' && !formData.partner_id) errors.partner_id = 'Veuillez sélectionner un partenaire associé';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs du formulaire');
      return;
    }
    setSaving(true);
    try {
      const submitData = {
        pseudo: formData.pseudo.trim(),
        prenom_utilisateur: formData.prenom_utilisateur.trim(),
        nom_utilisateur: formData.nom_utilisateur.trim(),
        email: formData.email.trim(),
        telephone: formData.telephone,
        date_naissance: formData.date_naissance || null,
        role: formData.role,
        partner_id: formData.role === 'partenaire' ? formData.partner_id : null,
        is_active: formData.is_active,
        is_validated: formData.is_validated,
        nom_magasin: formData.nom_magasin,
        raison_sociale: formData.raison_sociale,
        siret: formData.siret || null,
        adresse_magasin: formData.adresse_magasin,
        code_postal: formData.code_postal,
        ville: formData.ville,
        date_creation_magasin: formData.date_creation_magasin || null,
        zone_implantation: formData.zone_implantation,
        surface_magasin: formData.surface_magasin ? parseInt(formData.surface_magasin) : null,
        nb_salaries: formData.nb_salaries ? parseInt(formData.nb_salaries) : null,
        ca_magasin: formData.ca_magasin ? parseFloat(formData.ca_magasin) : null,
        jeu_libre: formData.jeu_libre,
        animations_magasin: formData.animations_magasin,
        capacite_accueil: formData.capacite_accueil ? parseInt(formData.capacite_accueil) : null,
        site_ecommerce: formData.site_ecommerce,
        newsletter: formData.newsletter,
      };
      if (formData.password && formData.password.trim()) {
        submitData.password = formData.password;
      }
      await api.put(`/users/${userId}`, submitData);
      toast.success('Utilisateur mis à jour avec succès !');
      navigate('/dashboard/admin/utilisateurs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardWrapper>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/admin/utilisateurs')} data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold" data-testid="edit-user-title">
              Modifier l'utilisateur
            </h1>
            <p className="text-muted-foreground mt-1">{user?.email}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border overflow-hidden">
          <form onSubmit={handleSubmit}>
            <div className="max-h-[calc(100vh-280px)] overflow-y-auto p-6 sm:p-8 space-y-6">

              {/* ═══════ Section 1: Identité ═══════ */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="h-5 w-5 text-primary" />
                    Identité
                  </CardTitle>
                  <CardDescription>Informations personnelles</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* 1. Pseudo * */}
                  <div className="space-y-2">
                    <Label htmlFor="pseudo">
                      Pseudo <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="pseudo"
                      name="pseudo"
                      value={formData.pseudo}
                      onChange={handleChange}
                      className={fieldErrors.pseudo ? 'border-red-500' : ''}
                      placeholder="MonPseudo"
                      data-testid="edit-pseudo-input"
                    />
                    {fieldErrors.pseudo && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />{fieldErrors.pseudo}
                      </p>
                    )}
                  </div>

                  {/* 2. Prénom * / 3. Nom * */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="prenom_utilisateur">
                        Prénom <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="prenom_utilisateur"
                        name="prenom_utilisateur"
                        value={formData.prenom_utilisateur}
                        onChange={handleChange}
                        className={fieldErrors.prenom_utilisateur ? 'border-red-500' : ''}
                        placeholder="Jean"
                        data-testid="edit-prenom-input"
                      />
                      {fieldErrors.prenom_utilisateur && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />{fieldErrors.prenom_utilisateur}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nom_utilisateur">
                        Nom <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="nom_utilisateur"
                        name="nom_utilisateur"
                        value={formData.nom_utilisateur}
                        onChange={handleChange}
                        className={fieldErrors.nom_utilisateur ? 'border-red-500' : ''}
                        placeholder="Dupont"
                        data-testid="edit-nom-input"
                      />
                      {fieldErrors.nom_utilisateur && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />{fieldErrors.nom_utilisateur}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* 4. Email professionnel */}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email professionnel</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@exemple.com"
                      data-testid="edit-email-input"
                    />
                  </div>

                  {/* 5. Téléphone / 6. Date de naissance */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="telephone" className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />Téléphone
                      </Label>
                      <Input
                        id="telephone"
                        name="telephone"
                        type="tel"
                        value={formData.telephone}
                        onChange={handleChange}
                        placeholder="06 12 34 56 78"
                        data-testid="edit-telephone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date_naissance" className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />Date de naissance
                      </Label>
                      <Input
                        id="date_naissance"
                        name="date_naissance"
                        type="date"
                        value={formData.date_naissance}
                        onChange={handleChange}
                        data-testid="edit-date-naissance-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* ═══════ Section 2: Informations système ═══════ */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Informations système</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="role">Rôle</Label>
                      <Select
                        value={formData.role}
                        onValueChange={(value) => handleSelectChange('role', value)}
                      >
                        <SelectTrigger data-testid="edit-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="retailer">Magasin</SelectItem>
                          <SelectItem value="partenaire">Partenaire</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center space-x-2 pt-7">
                      <Checkbox
                        id="is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                        data-testid="edit-is-active-checkbox"
                      />
                      <Label htmlFor="is_active" className="text-sm font-normal cursor-pointer">Compte actif</Label>
                    </div>

                    <div className="flex items-center space-x-2 pt-7">
                      <Checkbox
                        id="is_validated"
                        checked={formData.is_validated}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_validated: checked }))}
                        data-testid="edit-is-validated-checkbox"
                      />
                      <Label htmlFor="is_validated" className="text-sm font-normal cursor-pointer">Compte validé</Label>
                    </div>
                  </div>

                  {/* Section Partenaire */}
                  {formData.role === 'partenaire' && (
                    <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-amber-600" />
                        <h4 className="font-semibold text-amber-800">Rattachement Partenaire</h4>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="partner_id">
                          Partenaire associé <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={formData.partner_id}
                          onValueChange={(value) => handleSelectChange('partner_id', value)}
                        >
                          <SelectTrigger data-testid="edit-partner-select" className={`bg-white ${fieldErrors.partner_id ? 'border-red-500' : ''}`}>
                            <SelectValue placeholder="Sélectionnez un partenaire" />
                          </SelectTrigger>
                          <SelectContent>
                            {partners.map((partner) => (
                              <SelectItem key={partner.id} value={partner.id}>
                                {partner.nom} {partner.slug && `(${partner.slug})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {fieldErrors.partner_id && (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />{fieldErrors.partner_id}
                          </p>
                        )}
                        {formData.partner_id && (
                          <p className="text-xs text-muted-foreground">
                            ID: <code className="bg-gray-100 px-1 rounded">{formData.partner_id}</code>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="password">
                      Nouveau mot de passe
                      <span className="text-xs text-muted-foreground ml-2">(laisser vide pour ne pas modifier)</span>
                    </Label>
                    <PasswordInput
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      data-testid="edit-password-input"
                      autoComplete="new-password"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* ═══════ Section 3: Informations du magasin (retailers) ═══════ */}
              {formData.role === 'retailer' && (
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Store className="h-5 w-5 text-primary" />
                      Informations du magasin
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* 1. Nom du magasin / 2. Raison sociale * */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="nom_magasin">Nom du magasin</Label>
                        <Input
                          id="nom_magasin"
                          name="nom_magasin"
                          value={formData.nom_magasin}
                          onChange={handleChange}
                          placeholder="La Caverne du Meeple"
                          data-testid="edit-nom-magasin-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="raison_sociale">
                          Raison sociale <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="raison_sociale"
                          name="raison_sociale"
                          value={formData.raison_sociale}
                          onChange={handleChange}
                          placeholder="SARL DUPONT JEUX"
                          data-testid="edit-raison-sociale-input"
                        />
                      </div>
                    </div>

                    {/* 3. Numéro SIRET */}
                    <div className="space-y-2">
                      <Label htmlFor="siret">Numéro SIRET</Label>
                      <Input
                        id="siret"
                        name="siret"
                        value={formData.siret}
                        onChange={handleChange}
                        placeholder="14 chiffres"
                        maxLength={14}
                        inputMode="numeric"
                        data-testid="edit-siret-input"
                      />
                      <p className="text-xs text-muted-foreground">14 chiffres exactement</p>
                    </div>

                    {/* 4. Adresse */}
                    <div className="space-y-2">
                      <Label htmlFor="adresse_magasin" className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />Adresse
                      </Label>
                      <Input
                        id="adresse_magasin"
                        name="adresse_magasin"
                        value={formData.adresse_magasin}
                        onChange={handleChange}
                        data-testid="edit-adresse-input"
                      />
                    </div>

                    {/* 5. Code postal / 6. Ville */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="code_postal">Code postal</Label>
                        <Input
                          id="code_postal"
                          name="code_postal"
                          value={formData.code_postal}
                          onChange={handleChange}
                          data-testid="edit-code-postal-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ville">Ville</Label>
                        <Input
                          id="ville"
                          name="ville"
                          value={formData.ville}
                          onChange={handleChange}
                          data-testid="edit-ville-input"
                        />
                      </div>
                    </div>

                    {/* 7. Date de création du magasin / 8. Zone d'implantation */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="date_creation_magasin" className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />Date de création du magasin
                        </Label>
                        <Input
                          id="date_creation_magasin"
                          name="date_creation_magasin"
                          type="date"
                          value={formData.date_creation_magasin}
                          onChange={handleChange}
                          data-testid="edit-date-creation-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="zone_implantation">Zone d'implantation</Label>
                        <Select
                          value={formData.zone_implantation}
                          onValueChange={(value) => handleSelectChange('zone_implantation', value)}
                        >
                          <SelectTrigger data-testid="edit-zone-select">
                            <SelectValue placeholder="Sélectionnez une zone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="centre ville">Centre ville</SelectItem>
                            <SelectItem value="périférie">Périphérie</SelectItem>
                            <SelectItem value="centre commercial">Centre commercial</SelectItem>
                            <SelectItem value="zone rural">Zone rurale</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* 9. Surface / 10. Nb salariés / 11. CA annuel */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="surface_magasin">Surface (m²)</Label>
                        <Input
                          id="surface_magasin"
                          name="surface_magasin"
                          type="number"
                          value={formData.surface_magasin}
                          onChange={handleChange}
                          data-testid="edit-surface-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="nb_salaries" className="flex items-center gap-2">
                          <Users className="h-4 w-4" />Nb salariés
                        </Label>
                        <Input
                          id="nb_salaries"
                          name="nb_salaries"
                          type="number"
                          value={formData.nb_salaries}
                          onChange={handleChange}
                          data-testid="edit-nb-salaries-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ca_magasin" className="flex items-center gap-2">
                          <Euro className="h-4 w-4" />CA annuel (€)
                        </Label>
                        <Input
                          id="ca_magasin"
                          name="ca_magasin"
                          type="number"
                          value={formData.ca_magasin}
                          onChange={handleChange}
                          data-testid="edit-ca-input"
                        />
                      </div>
                    </div>

                    {/* 12. Jeu libre en magasin */}
                    <div className="space-y-2">
                      <Label htmlFor="jeu_libre">Jeu libre en magasin</Label>
                      <p className="text-xs text-muted-foreground">Le magasin permet-il de jouer librement sur place ?</p>
                      <Select
                        key={`jeu_libre_${formData.jeu_libre === true ? 'T' : formData.jeu_libre === false ? 'F' : 'U'}`}
                        defaultValue={formData.jeu_libre === true ? 'true' : formData.jeu_libre === false ? 'false' : '__unset__'}
                        onValueChange={(v) => {
                          const mapped = v === 'true' ? true : v === 'false' ? false : null;
                          if (mapped !== formData.jeu_libre) {
                            setFormData(prev => ({ ...prev, jeu_libre: mapped }));
                          }
                        }}
                      >
                        <SelectTrigger data-testid="edit-jeu-libre-select">
                          <SelectValue placeholder="Non renseigné" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unset__">Non renseigné</SelectItem>
                          <SelectItem value="true">Oui</SelectItem>
                          <SelectItem value="false">Non</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* 13. Animations / 14. Capacité d'accueil */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="animations_magasin">Animations et événements</Label>
                        <p className="text-xs text-muted-foreground">Le magasin organise-t-il des animations ?</p>
                        <Select
                          key={`animations_${formData.animations_magasin === true ? 'T' : formData.animations_magasin === false ? 'F' : 'U'}`}
                          defaultValue={formData.animations_magasin === true ? 'true' : formData.animations_magasin === false ? 'false' : '__unset__'}
                          onValueChange={(v) => {
                            const mapped = v === 'true' ? true : v === 'false' ? false : null;
                            if (mapped !== formData.animations_magasin) {
                              setFormData(prev => ({ ...prev, animations_magasin: mapped }));
                            }
                          }}
                        >
                          <SelectTrigger data-testid="edit-animations-select">
                            <SelectValue placeholder="Non renseigné" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__unset__">Non renseigné</SelectItem>
                            <SelectItem value="true">Oui</SelectItem>
                            <SelectItem value="false">Non</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="capacite_accueil">Capacité d'accueil</Label>
                        <Input
                          id="capacite_accueil"
                          name="capacite_accueil"
                          type="number"
                          min="0"
                          value={formData.capacite_accueil}
                          onChange={handleChange}
                          placeholder="Nombre de places"
                          data-testid="edit-capacite-input"
                        />
                        <p className="text-xs text-muted-foreground">Places pour animations/événements</p>
                      </div>
                    </div>

                    {/* 15. Site e-commerce */}
                    <div className="space-y-2">
                      <Label htmlFor="site_ecommerce" className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />Site e-commerce
                      </Label>
                      <Select
                        value={formData.site_ecommerce === true ? 'true' : formData.site_ecommerce === false ? 'false' : ''}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, site_ecommerce: value === 'true' ? true : value === 'false' ? false : null }))}
                      >
                        <SelectTrigger data-testid="edit-ecommerce-select" className="w-[200px]">
                          <SelectValue placeholder="Non renseigné" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Oui</SelectItem>
                          <SelectItem value="false">Non</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Newsletter */}
              <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg">
                <Checkbox
                  id="newsletter"
                  checked={formData.newsletter}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, newsletter: checked }))}
                  data-testid="edit-newsletter-checkbox"
                />
                <Label htmlFor="newsletter" className="text-sm font-normal cursor-pointer">Newsletter activée</Label>
              </div>
            </div>

            {/* Footer sticky */}
            <div className="border-t bg-gray-50 px-6 sm:px-8 py-4 flex flex-col sm:flex-row gap-3">
              <Button type="button" variant="outline" onClick={() => navigate('/dashboard/admin/utilisateurs')} className="flex-1 sm:flex-none" data-testid="cancel-button">
                Annuler
              </Button>
              <Button type="submit" className="flex-1 sm:flex-none" disabled={saving} data-testid="save-button">
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" />Enregistrer les modifications</>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DashboardWrapper>
  );
};

export default AdminEditUser;
